'use client';

import CompanyName from '@/components/react-hook-form/CompanyName';
import StayNow from '@/components/react-hook-form/StayNow';
import RegisterLayout from '@/app/register/components/RegisterLayout';
import { useFormContext, Controller } from 'react-hook-form';
import { FormSchema } from '@/app/register/schema';
import { useMemo } from 'react';
import { initialValue as numberOfJobChangeInitialValue } from '@/app/register/_numberOfJobChange/schema';
import { registerTextStyle } from '@/app/register/constants';
import { buildUpdateUserCareerParams } from '@/app/register/functions';
import { updateUserCareer } from '@/actions/user';
import FirebasePageViewEvent from '@/components/firebase/PageViewEvent';
import { EVENTS } from '@/lib/firebase/analytics/events';
import { SelectItem } from '@/components/shadcn/Select';
import WorkplaceDate from '@/components/react-hook-form/WorkplaceDate';

export const isLastWorkplace = (index: number, length: number) =>
  index === length - 1;

export const createYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 1960;

  return Array.from({ length: currentYear - startYear + 1 }).map((_, i) => (
    <SelectItem key={i} value={`${startYear + i}`}>
      {`${startYear + i}年`}
    </SelectItem>
  ));
};

export const createMonthOptions = () =>
  Array.from({ length: 12 }).map((_, i) => (
    <SelectItem key={i + 1} value={`${i + 1}`}>
      {`${i + 1}月`}
    </SelectItem>
  ));

export default function WorkplaceForm() {
  const { control, getValues, watch } = useFormContext<FormSchema>();

  const YearElements = useMemo(createYearOptions, []);
  const MonthElements = useMemo(createMonthOptions, []);

  const numberOfJobChange = getValues('numberOfJobChange');
  const items = useMemo(
    () =>
      Array(
        (numberOfJobChange === numberOfJobChangeInitialValue
          ? 0
          : numberOfJobChange) + 1
      ).fill(1),
    [numberOfJobChange]
  );

  const updateWorkplace = () => {
    const workplace = getValues('workplace');
    // 入力する企業は直近の1社のみなので、isLatestフラグをチェックする
    const workplaceWithIsLatest = {
      ...workplace[0],
      companyOrder: 1,
      isLatest: true
    };
    const params = buildUpdateUserCareerParams({
      workplace: [workplaceWithIsLatest]
    });
    return updateUserCareer(params);
  };

  return (
    <div>
      <h3 className={registerTextStyle}>
        <span className="text-[20px] text-[#231717]">勤務先企業名</span>
        を教えてください
      </h3>
      <RegisterLayout
        prev="prefecture"
        next="jobexperience"
        updateProfile={updateWorkplace}
      >
        <div className="mb-[16px] mt-[20px] text-left text-[12px] font-normal text-[#888] md:text-[14px]">
          ※ 直近の勤務先企業からの閲覧をブロックできます。
          <br />※ フリーランスの場合は「フリーランス」と入力してください。
        </div>
        {items.map((e, i) => (
          <div
            key={`${e}-${i}`}
            className="relative mx-auto mt-[24px] max-w-[672px]"
          >
            <Controller
              name={`workplace.${i}.companyName`}
              control={control}
              key={`${e}-${i}`}
              render={({ field: { value } }) => {
                return <CompanyName value={value} index={i} />;
              }}
            />

            <div className="mt-[13px]">
              <p className="mb-[8px] ml-[5px] text-[12px] font-semibold text-[#231717]">
                入社年月
              </p>
              <div className="mx-auto flex justify-center gap-[20px]">
                <Controller
                  control={control}
                  name={`workplace.${i}.joinedDate.year`}
                  render={({ field: { value, onChange } }) => (
                    <WorkplaceDate
                      value={value}
                      onChange={onChange}
                      optionElements={YearElements}
                      className="w-full data-[placeholder]:text-gray-400"
                      date="年"
                      placeholder="入社年"
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`workplace.${i}.joinedDate.month`}
                  render={({ field: { value, onChange } }) => (
                    <WorkplaceDate
                      value={value}
                      onChange={onChange}
                      optionElements={MonthElements}
                      className="w-full data-[placeholder]:text-gray-400"
                      date="月"
                      placeholder="入社月"
                    />
                  )}
                />
              </div>
            </div>

            <div className="mt-[13px]">
              <p className="mb-[8px] ml-[5px] text-[12px] font-semibold text-[#231717]">
                退社年月
              </p>
              <div className="mx-auto flex justify-center gap-[20px]">
                <Controller
                  control={control}
                  name={`workplace.${i}.retiredDate.year`}
                  render={({ field: { value, onChange } }) => (
                    <WorkplaceDate
                      value={value}
                      onChange={onChange}
                      optionElements={YearElements}
                      className="w-full data-[placeholder]:text-gray-400"
                      date="年"
                      placeholder="退社年"
                      disabled={watch(`workplace.${i}.stayNow`)}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`workplace.${i}.retiredDate.month`}
                  render={({ field: { value, onChange } }) => (
                    <WorkplaceDate
                      value={value}
                      onChange={onChange}
                      optionElements={MonthElements}
                      className="w-full data-[placeholder]:text-gray-400"
                      date="月"
                      placeholder="退社月"
                      disabled={watch(`workplace.${i}.stayNow`)}
                    />
                  )}
                />
              </div>
            </div>

            {isLastWorkplace(i, items.length) ? (
              <Controller
                name={`workplace.${i}.stayNow`}
                control={control}
                render={({ field: { value } }) => {
                  return <StayNow value={value} index={i} />;
                }}
              />
            ) : null}
          </div>
        ))}
      </RegisterLayout>

      {/* Firebaseイベント発火 */}
      <FirebasePageViewEvent
        eventName={EVENTS.registration_workplace_display}
      />
    </div>
  );
}
